/**
 * WeeklyHoursChart
 *
 * One timekeeper's week-by-week hours across the selected range, with their
 * weekly goal as a reference line. Shown when a row is expanded.
 *
 * A bar per week reads the shape of someone's period at a glance — a dip, a
 * ramp, a dead fortnight — which a column of numbers does not.
 */

import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

export interface WeeklyHoursPoint {
	label: string;
	hours: number;
	goal: number;
}

interface WeeklyHoursChartProps {
	data: WeeklyHoursPoint[];
	/** Flat weekly goal for the reference line; null when the title has none. */
	goal: number | null;
}

/**
 * Same pairing the Year-over-Year chart uses: the hero tone for the series
 * that is doing well, the receding one for the baseline it is measured
 * against. Kept in step with YOY_CHART_COLORS in config/dashboardTheme.ts.
 */
const AT_GOAL = "var(--chart-1)"; // brand green — YoY "after"
const BELOW_GOAL = "var(--chart-2)"; // brand blue — YoY "before"

function ChartTooltip({
	active,
	payload,
}: {
	active?: boolean;
	payload?: Array<{ payload: WeeklyHoursPoint }>;
}) {
	const point = payload?.[0]?.payload;
	if (!active || !point) return null;
	const delta = point.hours - point.goal;
	return (
		<div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-md">
			<p className="font-medium text-popover-foreground">{point.label}</p>
			<p className="text-muted-foreground">
				{point.hours.toFixed(1)} hrs
				{point.goal > 0 && ` of ${point.goal.toFixed(1)}`}
			</p>
			{point.goal > 0 && (
				<p
					className={
						delta >= 0
							? "text-emerald-600 dark:text-emerald-400"
							: "text-red-600 dark:text-red-400"
					}
				>
					{delta >= 0 ? "+" : ""}
					{delta.toFixed(1)} hrs
				</p>
			)}
		</div>
	);
}

export default function WeeklyHoursChart({
	data,
	goal,
}: WeeklyHoursChartProps) {
	return (
		<div className="h-56 w-full">
			<ResponsiveContainer width="100%" height="100%">
				<BarChart
					data={data}
					margin={{ top: 8, right: 16, bottom: 4, left: 0 }}
					barCategoryGap="20%"
				>
					<CartesianGrid
						strokeDasharray="3 3"
						vertical={false}
						stroke="var(--border)"
					/>
					<XAxis
						dataKey="label"
						tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
						tickLine={false}
						axisLine={{ stroke: "var(--border)" }}
						interval="preserveStartEnd"
					/>
					<YAxis
						tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
						tickLine={false}
						axisLine={false}
						width={44}
						unit="h"
					/>
					<Tooltip
						content={<ChartTooltip />}
						cursor={{ fill: "var(--muted)", opacity: 0.4 }}
					/>
					{goal !== null && goal > 0 && (
						<ReferenceLine
							y={goal}
							stroke="var(--foreground)"
							strokeWidth={2}
							strokeDasharray="7 4"
							strokeOpacity={0.85}
							// Without this the line is clipped when every bar sits below
							// the goal — exactly the case you most need to see it.
							ifOverflow="extendDomain"
							label={{
								value: `Goal ${goal.toFixed(1)}h`,
								position: "insideTopRight",
								fontSize: 11,
								fontWeight: 600,
								fill: "var(--foreground)",
							}}
						/>
					)}
					<Bar dataKey="hours" radius={[3, 3, 0, 0]}>
						{data.map((d) => (
							<Cell
								key={d.label}
								fill={
									goal !== null && goal > 0 && d.hours < goal
										? BELOW_GOAL
										: AT_GOAL
								}
							/>
						))}
					</Bar>
				</BarChart>
			</ResponsiveContainer>
		</div>
	);
}
